import { useTranslation } from '@e-kyc/translations';

import { Button, ExclamationIcon, LoadingSpinner, Modal } from '../../../../../components';
import type { EkycStep } from '../../../../../providers';

interface CaptureFrontModalProps {
  isOpen: boolean;
  error: 'DuplicatedIdNumber' | 'Other' | null;
  isLoading: boolean;
  setError: (error: 'DuplicatedIdNumber' | 'Other' | null) => void;
  setStep: (step: EkycStep) => void;
  resetCamera: () => void;
  close: () => void;
}

function CaptureFrontModal(props: CaptureFrontModalProps) {
  const { isOpen, error, isLoading, setError, resetCamera, setStep, close } = props;

  const { t } = useTranslation();

  const errorCasesMap = {
    DuplicatedIdNumber: (
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='flex flex-col items-center justify-center gap-4 p-6 pt-10'>
          <ExclamationIcon />
          <div className='flex flex-col items-center justify-center gap-2'>
            <h5 className='text-error mt-4 font-semibold'>
              {t('capture.modal.front.failed.duplicated.title')}
            </h5>
            <div className='text-body-1 text-text-secondary text-center'>
              {t('capture.modal.failed.duplicated.description')}
            </div>
          </div>
        </div>
        <div className='w-full p-4'>
          <Button
            className='w-full'
            onClick={() => {
              close();
              setError(null);
              setStep('onboarding');
            }}
          >
            {t('button.acknowledge')}
          </Button>
        </div>
      </div>
    ),
    Other: (
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='flex flex-col items-center justify-center gap-4 p-6 pt-10'>
          <img src='id_front_failed.png' alt='front id failed icon' />
          <div className='flex flex-col items-center justify-center gap-2'>
            <h5 className='text-error mt-4 font-semibold'>
              {t('capture.modal.front.failed.title')}
            </h5>
            <div className='text-body-1 text-text-secondary text-center'>
              {t('capture.modal.failed.description')}
            </div>
          </div>
        </div>
        <div className='w-full p-4'>
          <Button
            className='w-full'
            onClick={() => {
              close();
              resetCamera();
            }}
          >
            {t('button.retry')}
          </Button>
        </div>
      </div>
    ),
  };

  return (
    <Modal open={isOpen} className='w-full max-w-[343px]'>
      {error ? (
        errorCasesMap[error]
      ) : !isLoading ? (
        <div className='flex w-full flex-col items-center justify-center'>
          <div className='flex flex-col items-center justify-center gap-4 p-6 pt-10'>
            <img src='id_front_passed.png' alt='front id passed icon' />
            <div className='flex flex-col items-center justify-center gap-2'>
              <h5 className='mt-4 font-semibold'>{t('capture.modal.front.success.title')}</h5>
              <div className='text-body-1 text-text-secondary'>
                {t('capture.modal.front.success.description')}
              </div>
            </div>
          </div>
          <div className='w-full p-4'>
            <Button
              className='w-full'
              onClick={() => {
                setStep('capture-id-card-back');
              }}
            >
              {t('button.next')}
            </Button>
          </div>
        </div>
      ) : (
        <div className='flex w-full flex-col items-center justify-center gap-4 py-6'>
          <div className='flex size-12 flex-col items-center justify-center'>
            <LoadingSpinner />
          </div>
          <h5>{t('capture.modal.loading')}</h5>
        </div>
      )}
    </Modal>
  );
}

export default CaptureFrontModal;
